import React from 'react';
import { includes, orderBy } from 'lodash';

import { LocaleService, ConfigsService } from '@sm360/widget-commons-core-services';
import {
    FIELD_LENGTH,
    InputText,
    InputEmail,
    InputPhoneNumber,
    StringUtilService,
    ValidationService,
    InputDropDown,
    COUNTRIES,
} from '@sm360/widget-commons-forms';

import LoadingButton from 'login/components/Library/LoadingButton/LoadingButton.jsx';
import RadioButtonContainer from 'login/components/Inputs/RadioButton/RadioButtonContainer.jsx';
import PrivacyPolicy from 'login/components/Library/PrivacyPolicy/PrivacyPolicy.jsx';
import InputZipCode from 'login/components/Inputs/InputZipCode/InputZipCode.jsx';

import { ProfileService, AnalyticsService } from '@sm360/widget-commons-general';

import CONSTANT from 'login/constants/constant.js';
import TAGS from 'login/constants/trackingTags';

class ProfileForm extends React.Component {
    constructor(props) {
        super(props);

        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleGenderChange = this.handleGenderChange.bind(this);
        this.handleDropdownChange = this.handleDropdownChange.bind(this);
        this.handleFormSubmission = this.handleFormSubmission.bind(this);
        this.handlePhoneNumberChange = this.handlePhoneNumberChange.bind(this);
        this.handleZipCodeChange = this.handleZipCodeChange.bind(this);

        this.genderOptions = [
            {
                value: 0,
                label: 'LOGIN.PROFILE.GENDER_UNKNOWN',
            },
            {
                value: 1,
                label: 'LOGIN.PROFILE.GENDER_MALE',
            },
            {
                value: 2,
                label: 'LOGIN.PROFILE.GENDER_FEMALE',
            },
        ];

        this.country = ConfigsService.getCountry();

        this.provinces = orderBy(
            COUNTRIES.filter((prov) => prov.country === this.country).map((option) => {
                return {
                    value: option.code,
                    label: LocaleService.t(option.transKey),
                };
            }),
            ['value']
        );

        this.fieldRequired = true;
        this.emailDisabled = false;
        this.fieldRequired = ['firstname', 'lastname', 'email', 'email1'];

        this.state = {
            errorMessage: '',
            successMessage: '',
            btnEnabled: true,
            formValidation: {
                firstname: true,
                lastname: true,
                email: true,
                homePhone: true,
                mobilePhone: true,
                address: true,
                address2: true,
                city: true,
                province: true,
                postalCode: true,
            },
            profile: {},
        };
    }

    componentDidMount() {
        ProfileService.getLoggedAccountProfile().then((profile) => {
            if (profile && profile.province) {
                profile.province = profile.province.toLowerCase();
            }
            this.setState({ profile });
        });
    }

    getErrorMessageCode(response) {
        if (response && response.status === 409) {
            return CONSTANT.REGISTRATION_ERROR_KEYS.EMAIL_ALREADY_REGISTERED;
        }

        return CONSTANT.PROFILE_ERROR_KEYS.UNKNOWN_ERROR;
    }

    handleZipCodeChange(zipcodeResponse) {
        const { profile, formValidation } = this.state;

        profile.postalCode = zipcodeResponse.value;
        formValidation.postalCode = zipcodeResponse.validation;

        this.setState({
            profile,
            formValidation,
        });
    }

    handlePhoneNumberChange(phoneNumberResponse) {
        const { profile, formValidation } = this.state;
        const { name } = phoneNumberResponse;

        profile[name] = phoneNumberResponse.value;
        formValidation[name] = phoneNumberResponse.validation;

        this.setState({
            profile,
            formValidation,
        });
    }

    handleInputChange(value, name) {
        const { profile, formValidation } = this.state;

        // Update profile based on the input name
        if (name === 'email') {
            profile.email1 = value;
        } else {
            profile[name] = value;
        }

        if (includes(this.fieldRequired, name)) {
            if (name === 'email') {
                formValidation.email1 = ValidationService.validateEmail(value);
                profile.username = value;
            } else {
                formValidation[name] = ValidationService.validateIsNotEmpty(value);
            }
        } else {
            formValidation[name] = true;
        }

        this.setState({
            profile,
            formValidation,
        });
    }

    handleGenderChange(value) {
        const { profile } = this.state;
        profile.sexId = parseInt(value, 10);

        this.setState({
            profile,
        });
    }

    handleDropdownChange(option) {
        const { profile } = this.state;
        profile.province = option.value;

        this.setState({
            profile,
        });
    }

    handleFormSubmission() {
        if (!ValidationService.validateForm(this.state.formValidation)) {
            return null;
        }

        const { profile } = this.state;
        const { homePhone, mobilePhone } = profile;

        this.setState({
            errorMessage: '',
            successMessage: '',
            btnEnabled: false,
        });

        profile.postalCode = StringUtilService.removeSpaces(profile.postalCode).toLowerCase();

        if (homePhone) {
            profile.homePhone = `+1${homePhone}`;
        }

        if (mobilePhone) {
            profile.mobilePhone = `+1${mobilePhone}`;
        }

        return ProfileService.updateAccountProfile(profile)
            .then(() => {
                this.setState({
                    successMessage: LocaleService.t('LOGIN.SUCCESS_MESSAGE.PROFILE_UPDATE_SUCCESS'),
                });
            })
            .catch((error) => {
                const errorCodeKey = this.getErrorMessageCode(error.response);

                this.setState({
                    errorMessage: LocaleService.t(errorCodeKey),
                });
            })
            .then(() => {
                this.setState({
                    btnEnabled: true,
                });

                AnalyticsService.gaTrigger(TAGS.LOGIN_WIDGET__SAVE_ACCOUNT());
            });
    }

    handleSubmit(event) {
        event.preventDefault();
    }

    render() {
        return (
            <div>
                <h2 className="sr-subtitle-2">1. {LocaleService.t('LOGIN.PERSONAL_INFORMATION_SECTION_TITLE')}</h2>
                <form onSubmit={this.handleSubmit} id="profileForm">
                    <RadioButtonContainer
                        name="sexId"
                        options={this.genderOptions}
                        selected={this.state.profile.sexId}
                        valueChangeCallback={this.handleGenderChange}
                    />

                    <InputText
                        type="text"
                        name="firstname"
                        classNames={['styled-placeholder half']}
                        value={this.state.profile.firstname}
                        label={LocaleService.t('LOGIN.PROFILE.FIRST_NAME')}
                        isRequired={this.fieldRequired}
                        isValid={this.state.formValidation.firstname}
                        maxLength={FIELD_LENGTH.FIRST_NAME_MAX}
                        valueChange={this.handleInputChange}
                    />

                    <InputText
                        type="text"
                        name="lastname"
                        classNames={['styled-placeholder half']}
                        value={this.state.profile.lastname}
                        label={LocaleService.t('LOGIN.PROFILE.LAST_NAME')}
                        isRequired={this.fieldRequired}
                        isValid={this.state.formValidation.lastname}
                        maxLength={FIELD_LENGTH.LAST_NAME_MAX}
                        valueChange={this.handleInputChange}
                    />

                    <InputPhoneNumber
                        name="homePhone"
                        classNames={['styled-placeholder half']}
                        value={this.state.profile.homePhone}
                        label={LocaleService.t('LOGIN.PROFILE.HOME_PHONE_NUMBER')}
                        isValid={this.state.formValidation.homePhone}
                        valueChange={this.handlePhoneNumberChange}
                    />

                    <InputPhoneNumber
                        name="mobilePhone"
                        classNames={['styled-placeholder half']}
                        value={this.state.profile.mobilePhone}
                        label={LocaleService.t('LOGIN.PROFILE.MOBILE_PHONE_NUMBER')}
                        isValid={this.state.formValidation.mobilePhone}
                        valueChange={this.handlePhoneNumberChange}
                    />

                    <InputEmail
                        classNames={['styled-placeholder full']}
                        value={this.state.profile.email1}
                        label={LocaleService.t('LOGIN.EMAIL')}
                        isRequired={this.fieldRequired}
                        isValid={this.state.formValidation.email}
                        isDisabled={this.emailDisabled}
                        valueChange={this.handleInputChange}
                    />

                    <InputText
                        type="text"
                        name="address"
                        classNames={['styled-placeholder full']}
                        value={this.state.profile.address}
                        label={LocaleService.t('LOGIN.PROFILE.ADDRESS')}
                        isValid={this.state.formValidation.address}
                        valueChange={this.handleInputChange}
                        maxLength={FIELD_LENGTH.ADDRESS_MAX}
                    />

                    <InputText
                        type="text"
                        name="address2"
                        classNames={['styled-placeholder full']}
                        value={this.state.profile.address2}
                        label={LocaleService.t('LOGIN.PROFILE.ADDRESS_2')}
                        isValid={this.state.formValidation.address2}
                        valueChange={this.handleInputChange}
                        maxLength={FIELD_LENGTH.ADDRESS_MAX}
                    />

                    <InputText
                        type="text"
                        name="city"
                        classNames={['styled-placeholder third']}
                        label={LocaleService.t('LOGIN.PROFILE.CITY')}
                        value={this.state.profile.city}
                        maxLength={FIELD_LENGTH.CITY_MAX}
                        isValid={this.state.formValidation.city}
                        valueChange={this.handleInputChange}
                    />

                    <InputDropDown
                        name="province"
                        classNames={['styled-placeholder third']}
                        options={this.provinces}
                        value={this.state.profile.province}
                        placeholder={
                            this.country === 'us'
                                ? LocaleService.t('LOGIN.PROFILE.STATE')
                                : LocaleService.t('LOGIN.PROFILE.PROVINCE')
                        }
                        valueChange={this.handleDropdownChange}
                    />

                    <InputZipCode
                        classNames={['styled-placeholder third']}
                        value={this.state.profile.postalCode}
                        isValid={this.state.formValidation.postalCode}
                        valueChange={this.handleZipCodeChange}
                        country={this.country}
                    />

                    <div className="profile-form__error-message">{this.state.errorMessage}</div>
                    <div>{this.state.successMessage}</div>

                    <PrivacyPolicy />

                    <LoadingButton
                        text={LocaleService.t('LOGIN.SAVE')}
                        btnCallback={this.handleFormSubmission}
                        btnEnabled={this.state.btnEnabled}
                    />
                </form>
            </div>
        );
    }
}

ProfileForm.displayName = 'ProfileForm';

export default ProfileForm;
